import {
  Button,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormikProps } from 'formik';
import {
  FieldAttributesOptions,
  FieldTemplate,
  OptionSourceType,
} from 'api/models/FieldTemplate';
import { Add, Delete } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { GeneralIds, ModelTypes } from 'types/types';
import useRequest from 'hooks/useRequest';
import { useState } from 'react';
import { query } from 'operations';
import { AddFieldValues } from '../AddFieldTemplateDialog';

interface SelectOptionsEditorProps {
  disabled: boolean;
  formik: FormikProps<AddFieldValues>;
  fieldId: string;
}

export default function SelectOptionsEditor({
  disabled,
  formik,
  fieldId,
}: SelectOptionsEditorProps) {
  const { t } = useTranslation();
  const { projectId } = useParams<GeneralIds>();
  const [isEditSourceOption, setIsEditSourceOption] = useState(false);
  const [isCopySourceOption, setIsCopySourceOption] = useState(false);
  const [options, setOptionsState] = useState<FieldAttributesOptions[]>(
    formik.values.options ?? [
      {
        label: '',
        value: '',
      },
    ],
  );

  const {
    options: ownOptions,
    optionsSourceType,
    optionsSourceFieldTemplateId,
    alias,
  } = formik.values;

  useRequest<FieldAttributesOptions[]>(
    async () => {
      if (!optionsSourceFieldTemplateId) {
        const { data: fields } = await query<FieldTemplate[]>({
          type: ModelTypes.fieldTemplate,
          ids: {
            projectId,
            id: fieldId,
          },
        });
        const field = fields[0];
        if ((field?.fieldAttributes.options?.length ?? 0) > 0) {
          return setOptions(field?.fieldAttributes.options ?? []);
        }
      }

      const { data: fields } = await query<FieldTemplate[]>({
        type: ModelTypes.fieldTemplate,
        ids: {
          projectId,
          id: optionsSourceFieldTemplateId,
        },
      });

      const field = fields[0];
      if (
        (field?.fieldAttributes.options?.length ?? 0) > 0 &&
        optionsSourceFieldTemplateId &&
        field.alias !== alias
      ) {
        setOptions(field.fieldAttributes.options ?? []);
      } else {
        setOptions(ownOptions ?? []);
      }
    },
    {
      dependencies: [optionsSourceFieldTemplateId],
    },
  );

  const setOptions = (newOptions: FieldAttributesOptions[]) => {
    if (isEditSourceOption) {
      formik.setFieldValue('referedFieldsOptions', newOptions);
    } else if (!optionsSourceFieldTemplateId) {
      formik.setFieldValue('options', newOptions);
    }
    setOptionsState(newOptions);
  };

  const getError = (index: number, key: string) => {
    if (
      formik.errors.options &&
      formik.errors.options[index] &&
      // have to ignore this error since we are using formik in a wrong way
      // formik.errors.options[index] is a string, but we are validating an object array here
      // @ts-ignore
      formik.errors.options[index][key]
    ) {
      return true;
    }
    return false;
  };

  const getHelperText = (index: number, key: string) => {
    if (
      formik.errors.options &&
      formik.errors.options[index] &&
      // have to ignore this error since we are using formik in a wrong way
      // formik.errors.options[index] is a string, but we are validating an object array here
      // @ts-ignore
      formik.errors.options[index][key]
    ) {
      // @ts-ignore
      return formik.errors.options[index][key];
    }
    return '';
  };

  return optionsSourceType !== OptionSourceType.CodeLibrary ? (
    <>
      <Grid container p={2}>
        <Grid item xs={2}>
          <Typography variant={'subtitle1'}>
            {t('forms.fieldAttributes.options.titlePossibleValues')}
          </Typography>
        </Grid>
        <Grid item xs={5}>
          {optionsSourceFieldTemplateId && (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={isEditSourceOption}
                  disabled={disabled || isCopySourceOption}
                  onChange={e => {
                    setIsEditSourceOption(e.target.checked);
                  }}
                />
              }
              labelPlacement="start"
              label={t('forms.fieldAttributes.editSelectedOptions')}
            />
          )}
        </Grid>
        <Grid item xs={5}>
          {optionsSourceFieldTemplateId && (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={isCopySourceOption}
                  disabled={disabled || isEditSourceOption}
                  onChange={e => {
                    setIsCopySourceOption(e.target.checked);
                    if (e.target.checked) {
                      formik.setFieldValue(
                        'optionsSourceFieldTemplateId',
                        null,
                      );
                      formik.setFieldValue('optionsSourceFieldAlias', null);
                      formik.setFieldValue('optionsSourceCodeLibraryId', null);
                      formik.setFieldValue('optionsSourceType', null);
                      formik.setFieldValue('referedFieldsOptions', []);
                      formik.setFieldValue('options', options);
                    }
                  }}
                />
              }
              labelPlacement="start"
              label={t('forms.fieldAttributes.copySelectedOptions')}
            />
          )}
        </Grid>
      </Grid>
      <Grid item xs={12}>
        {options &&
          options?.map((option: FieldAttributesOptions, index: number) => (
            <Grid container spacing={2} key={index} mb={1}>
              <Grid item xs={5}>
                <TextField
                  disabled={
                    disabled ||
                    Boolean(!isEditSourceOption && optionsSourceFieldTemplateId)
                  }
                  sx={{ mt: 1 }}
                  fullWidth
                  label={`${t('forms.fieldAttributes.label')} *`}
                  type="text"
                  name="label"
                  value={option.label}
                  onChange={e => {
                    const val = e.target.value;
                    const newOptions = [...options];
                    newOptions[index].label = val;
                    setOptions(newOptions);
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  error={getError(index, 'label')}
                  helperText={getHelperText(index, 'label')}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  disabled={
                    disabled ||
                    Boolean(!isEditSourceOption && optionsSourceFieldTemplateId)
                  }
                  sx={{ mt: 1 }}
                  fullWidth
                  label={`${t('forms.fieldAttributes.options.optionValue')} *`}
                  type="text"
                  name="value"
                  value={option.value}
                  onChange={e => {
                    const val = e.target.value;
                    const newOptions = [...options];
                    newOptions[index].value = val;
                    setOptions(newOptions);
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  error={getError(index, 'value')}
                  helperText={getHelperText(index, 'value')}
                />
              </Grid>
              <Grid item xs={1} alignItems={'center'} display={'flex'}>
                <IconButton
                  size="small"
                  onClick={() => {
                    const newOptions = [...options];
                    newOptions.splice(index, 1);
                    setOptions(newOptions);
                  }}
                  disabled={
                    options.length === 1 ||
                    disabled ||
                    Boolean(!isEditSourceOption && optionsSourceFieldTemplateId)
                  }
                >
                  <Tooltip title={t('general.delete')}>
                    <Delete fontSize="small" />
                  </Tooltip>
                </IconButton>
              </Grid>
            </Grid>
          ))}
      </Grid>
      <Grid item xs={12} justifyContent={'end'} display={'flex'}>
        <Button
          disabled={
            disabled ||
            Boolean(!isEditSourceOption && optionsSourceFieldTemplateId)
          }
          onClick={() => {
            const newOptions = [...options];
            newOptions.push({ label: '', value: '' });
            setOptions(newOptions);
          }}
          startIcon={<Add />}
        >
          {t('forms.fieldAttributes.options.addNewOption')}
        </Button>
      </Grid>
    </>
  ) : (
    <></>
  );
}
